import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, File, CheckCircle } from 'lucide-react';
import { apiClient } from '@/api/axios';

interface UploadDocumentModalProps {
  onClose: () => void;
  onUpload: (document: {
    name: string;
    type: string;
    date: string;
    file: File;
    notes?: string;
  }) => void;
}

const documentTypes = [
  { value: 'vaccination-record', labelKey: 'documents.types.vaccination' as const },
  { value: 'health-certificate', labelKey: 'documents.types.healthCertificate' as const },
  { value: 'dna-report', labelKey: 'documents.types.dnaReport' as const },
  { value: 'ownership-record', labelKey: 'documents.types.ownershipRecord' as const },
  { value: 'microchip-certificate', labelKey: 'documents.types.microchipCertificate' as const },
  { value: 'pedigree-certificate', labelKey: 'documents.types.pedigreeCertificate' as const },
  { value: 'insurance-document', labelKey: 'documents.types.insurance' as const },
  { value: 'medical-record', labelKey: 'documents.types.medical' as const },
  { value: 'lab-result', labelKey: 'documents.types.labResult' as const },
  { value: 'x-ray-scan', labelKey: 'documents.types.xrayScan' as const },
  { value: 'surgical-report', labelKey: 'documents.types.surgicalReport' as const },
  { value: 'dental-record', labelKey: 'documents.types.dentalRecord' as const },
  { value: 'passport', labelKey: 'documents.types.passport' as const },
  { value: 'other', labelKey: 'documents.types.other' as const },
];

export function UploadDocumentModal({
  onClose,
  onUpload,
}: UploadDocumentModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    if (!formData.name) {
      // Auto-populate name from filename (remove extension)
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setFormData((prev) => ({ ...prev, name: nameWithoutExt }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    setError(null);
    setUploading(true);

    try {
      const contentType = selectedFile.type || 'application/octet-stream';

      // 1) Ask backend for an upload URL
      const initRes = await apiClient.post<{
        uploadUrl: string;
        key: string;
      }>('/documents/upload-init', {
        filename: selectedFile.name,
        contentType,
        folderPrefix: formData.type ? `${formData.type}/` : undefined,
        ttlSeconds: 900,
        displayName: formData.name.trim() || undefined,
      });

      const { uploadUrl } = initRes.data;

      // 2) Upload file to backend (proxied to R2)
      await apiClient.put(uploadUrl, selectedFile, {
        headers: { 'Content-Type': contentType },
      });

      // 3) Notify parent that the document metadata is ready
      onUpload({
        ...formData,
        file: selectedFile,
        notes: formData.notes || undefined,
      });

      onClose();
    } catch (err: unknown) {
      console.error('Upload failed', err);
      setError(
        err instanceof Error ? err.message : 'Upload failed. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl mb-1">{t('documents.uploadModal.title')}</h2>
              <p className="text-sm opacity-90">
                {t('documents.uploadModal.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded p-3">
                {error}
              </div>
            )}
            {/* File Upload Area */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : selectedFile
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-700/30'
                    : 'border-gray-300 dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]'
              }`}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  <div className="text-left">
                    <div className="text-green-900 dark:text-green-300">
                      {selectedFile.name}
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-400">
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="ml-4 px-3 py-1 text-sm border border-green-600 dark:border-green-700/30 text-green-600 dark:text-green-400 rounded hover:bg-green-100 dark:hover:bg-green-900/20"
                  >
                    {t('documents.uploadModal.removeFile')}
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-4" />
                  <div className="text-gray-700 dark:text-white/70 mb-2">
                    {t('documents.uploadModal.dragDropOrBrowse')}{' '}
                    <label className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer underline">
                      {t('documents.uploadModal.browse')}
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileInputChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                    </label>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-white/50">
                    {t('documents.uploadModal.supportsPdfJpgPng')}
                  </div>
                </>
              )}
            </div>

            {/* Document Type */}
            <div>
              <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
                {t('documents.uploadModal.documentType')} <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#0F0E16] dark:text-white"
              >
                <option value="">{t('documents.uploadModal.selectDocumentType')}</option>
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {t(type.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Name */}
            <div>
              <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
                {t('documents.uploadModal.documentName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#0F0E16] dark:text-white"
                placeholder={t('documents.uploadModal.documentNamePlaceholder')}
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
                {t('documents.uploadModal.documentDate')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#0F0E16] dark:text-white"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
                {t('documents.uploadModal.notesOptional')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-[#0F0E16] dark:text-white"
                placeholder={t('documents.uploadModal.notesOptionalPlaceholder')}
              />
            </div>

            {/* Blockchain Info */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <File className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                <div className="text-sm text-purple-800 dark:text-purple-300">
                  <div className="mb-1">{t('documents.uploadModal.blockchainStorage')}</div>
                  <div className="text-xs text-purple-700 dark:text-purple-400">
                    {t('documents.uploadModal.blockchainStorageDesc')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-white/8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/8 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors dark:text-white"
            >
              {t('documents.uploadModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={!selectedFile || uploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  {t('documents.uploadModal.uploading')}
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  {t('documents.uploadModal.uploadDocument')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
